<div class="space-y-6">

    <!-- Controls Bar: Time Range Presets + Interval + Export -->
    <div class="bg-white border border-slate-200 shadow-sm p-4 rounded-2xl flex flex-wrap items-center justify-between gap-4">
        <!-- Preset Timeframe Buttons -->
        <div class="flex flex-wrap items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
            @for (p of presets; track p.key) {
            <button type="button"
                    class="px-3 py-1.5 text-xs font-semibold rounded-lg transition-all"
                    [class.bg-white]="selectedPreset() === p.key"
                    [class.text-blue-700]="selectedPreset() === p.key"
                    [class.shadow-xs]="selectedPreset() === p.key"
                    [class.text-slate-600]="selectedPreset() !== p.key"
                    (click)="setPreset(p.key)">
                {{ p.labelKey | translate }}
            </button>
            }
        </div>

        <!-- Interval Selector & Export Button -->
        <div class="flex items-center gap-3">
            <div class="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
                @for (int of intervals; track int.key) {
                <button type="button"
                        class="px-2.5 py-1 text-xs font-medium rounded-lg transition-all"
                        [class.bg-white]="selectedInterval() === int.key"
                        [class.text-slate-900]="selectedInterval() === int.key"
                        [class.shadow-xs]="selectedInterval() === int.key"
                        [class.text-slate-500]="selectedInterval() !== int.key"
                        (click)="setInterval(int.key)">
                    {{ int.labelKey | translate }}
                </button>
                }
            </div>

            <jjames-button variant="outline" matIconLeft="download"
                           [loading]="isExporting()" [disabled]="isExporting()"
                           (clicked)="exportSummaryCsv()">
                {{ 'reports.exportSummary' | translate }}
            </jjames-button>
        </div>
    </div>

    <!-- Summary KPIs for Selected Period -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Collected in Period -->
        <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
                <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'reports.collected' | translate }}</p>
                <h3 class="text-2xl font-bold text-slate-900 mt-1">
                    {{ totalCollectedInPeriod() | currency: currencyCode() : 'symbol-narrow' : '1.2-2' }}
                </h3>
                <span class="inline-flex items-center text-xs text-emerald-600 font-medium mt-1">
                    <mat-icon class="!w-3 !h-3 !text-[12px]">trending_up</mat-icon>
                    Paid in period
                </span>
            </div>
            <div class="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                <mat-icon>payments</mat-icon>
            </div>
        </div>

        <!-- Outstanding Unpaid in Period -->
        <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
                <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'reports.unpaid' | translate }}</p>
                <h3 class="text-2xl font-bold text-slate-900 mt-1">
                    {{ totalUnpaidInPeriod() | currency: currencyCode() : 'symbol-narrow' : '1.2-2' }}
                </h3>
                <span class="inline-flex items-center text-xs text-amber-600 font-medium mt-1">
                    <mat-icon class="!w-3 !h-3 !text-[12px]">pending_actions</mat-icon>
                    Open / under review
                </span>
            </div>
            <div class="p-3 bg-amber-50 text-amber-600 rounded-xl">
                <mat-icon>hourglass_empty</mat-icon>
            </div>
        </div>

        <!-- Waived / Cancelled in Period -->
        <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
                <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'reports.waived' | translate }}</p>
                <h3 class="text-2xl font-bold text-slate-900 mt-1">
                    {{ totalWaivedInPeriod() | currency: currencyCode() : 'symbol-narrow' : '1.2-2' }}
                </h3>
                <span class="inline-flex items-center text-xs text-slate-500 font-medium mt-1">
                    <mat-icon class="!w-3 !h-3 !text-[12px]">money_off</mat-icon>
                    Written off
                </span>
            </div>
            <div class="p-3 bg-slate-100 text-slate-600 rounded-xl">
                <mat-icon>block</mat-icon>
            </div>
        </div>

        <!-- Recovery / Success Rate -->
        <div class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
                <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">Recovery Rate</p>
                <h3 class="text-2xl font-bold text-slate-900 mt-1">{{ collectionRate() }}</h3>
                <span class="inline-flex items-center text-xs text-indigo-600 font-medium mt-1">
                    <mat-icon class="!w-3 !h-3 !text-[12px]">check_circle</mat-icon>
                    Paid vs. unpaid
                </span>
            </div>
            <div class="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                <mat-icon>pie_chart</mat-icon>
            </div>
        </div>
    </div>

    <!-- Main Chart: Interactive Revenue & Receivables Trend -->
    <div class="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-slate-100">
            <div>
                <h2 class="text-base font-bold text-slate-900">{{ 'reports.trendTitle' | translate }}</h2>
                <p class="text-xs text-slate-500 mt-0.5">{{ 'reports.trendSubtitle' | translate }}</p>
            </div>

            <!-- Chart Legend -->
            <div class="flex items-center gap-4 text-xs font-medium">
                <div class="flex items-center gap-1.5">
                    <span class="w-3 h-3 rounded-xs bg-emerald-500"></span>
                    <span class="text-slate-600">{{ 'reports.collected' | translate }}</span>
                </div>
                <div class="flex items-center gap-1.5">
                    <span class="w-3 h-3 rounded-xs bg-amber-400"></span>
                    <span class="text-slate-600">{{ 'reports.unpaid' | translate }}</span>
                </div>
                <div class="flex items-center gap-1.5">
                    <span class="w-3 h-3 rounded-xs bg-slate-300"></span>
                    <span class="text-slate-600">{{ 'reports.waived' | translate }}</span>
                </div>
            </div>
        </div>

        @if (isLoading()) {
        <div class="flex items-center justify-center py-20">
            <mat-spinner diameter="36"></mat-spinner>
        </div>
        } @else if (chartBars().length === 0) {
        <div class="flex flex-col items-center justify-center py-16 text-slate-400">
            <mat-icon class="!w-10 !h-10 !text-[40px] text-slate-300 mb-2">bar_chart</mat-icon>
            <p class="text-sm font-medium">{{ 'reports.noTrendData' | translate }}</p>
        </div>
        } @else {
        <!-- SVG / CSS Bar Chart Container -->
        <div class="relative pt-6 pb-2">
            <!-- Active Hover Tooltip Card -->
            @if (activeBar(); as bar) {
            <div class="absolute top-0 right-4 bg-slate-900 text-white text-xs px-3.5 py-2 rounded-xl shadow-lg flex flex-wrap items-center gap-3 z-10 animate-fade-in pointer-events-none">
                <div class="font-semibold text-slate-200 border-r border-slate-700 pr-3">{{ bar.label }}</div>
                <div class="text-emerald-400 font-bold">Collected: {{ bar.collected | currency: currencyCode() : 'symbol-narrow' : '1.2-2' }}</div>
                <div class="text-amber-300 font-medium">Unpaid: {{ bar.unpaid | currency: currencyCode() : 'symbol-narrow' : '1.2-2' }}</div>
                <div class="text-slate-400">Waived: {{ bar.waived | currency: currencyCode() : 'symbol-narrow' : '1.2-2' }}</div>
                <div class="text-slate-300 border-l border-slate-700 pl-3">Tries: {{ bar.volume }}</div>
            </div>
            }

            <!-- Scrollable chart track wrapper to contain overflow -->
            <div class="overflow-x-auto overflow-y-hidden pb-2 pt-6 -mt-6 chart-scrollbar">
                <!-- Bars Grid -->
                <div class="flex items-end justify-between gap-1.5 h-64 border-b border-slate-200 px-2"
                     [style.min-width.px]="chartMinWidth()">
                    @for (bar of chartBars(); track bar.date) {
                    <div class="flex-1 min-w-[20px] max-w-[48px] flex flex-col items-center h-full justify-end group relative cursor-pointer"
                         (mouseenter)="activeBar.set(bar)"
                         (mouseleave)="activeBar.set(null)">
                        
                        <!-- Stacked Bar Column -->
                        <div class="w-full max-w-[32px] flex flex-col justify-end rounded-t-md overflow-hidden transition-all duration-150 group-hover:opacity-90 group-hover:scale-y-[1.02] origin-bottom"
                             [style.height.%]="bar.barHeightPct">
                            
                            <!-- Waived/Cancelled Portion (top) -->
                            @if (bar.waived > 0) {
                            <div class="w-full bg-slate-300" [style.height.%]="(bar.waived / (bar.collected + bar.unpaid + bar.waived)) * 100"></div>
                            }

                            <!-- Unpaid Portion (middle) -->
                            @if (bar.unpaid > 0) {
                            <div class="w-full bg-amber-400" [style.height.%]="(bar.unpaid / (bar.collected + bar.unpaid + bar.waived)) * 100"></div>
                            }

                            <!-- Collected Portion (bottom) -->
                            @if (bar.collected > 0) {
                            <div class="w-full bg-emerald-500" [style.height.%]="(bar.collected / (bar.collected + bar.unpaid + bar.waived)) * 100"></div>
                            }

                            @if (bar.collected === 0 && bar.unpaid === 0 && bar.waived === 0) {
                            <div class="w-full h-1 bg-slate-100 rounded-xs"></div>
                            }
                        </div>

                        <!-- X-Axis Label -->
                        <span class="text-[10px] text-slate-400 mt-2 truncate w-full max-w-[40px] text-center group-hover:text-slate-800 font-medium">
                            {{ bar.label }}
                        </span>
                    </div>
                    }
                </div>
            </div>
        </div>
        }
    </div>

    <!-- Secondary Visual Breakdown: Channel Distribution & Status Distribution -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <!-- 1. Revenue by Payment Channel / Rail -->
        <div class="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
            <div>
                <h3 class="text-base font-semibold text-slate-900">{{ 'reports.railDistTitle' | translate }}</h3>
                <p class="text-xs text-slate-500 mt-0.5">{{ 'reports.railDistSubtitle' | translate }}</p>
            </div>

            <!-- Segmented Progress Bar -->
            <div class="h-3 w-full bg-slate-100 rounded-full flex overflow-hidden">
                @for (rail of railShares(); track rail.rail) {
                @if (rail.sharePct > 0) {
                <div [class]="rail.colorClass"
                     [style.width.%]="rail.sharePct"
                     [title]="(rail.labelKey | translate) + ': ' + rail.sharePct.toFixed(1) + '%'">
                </div>
                }
                }
            </div>

            <!-- Channel Share Cards -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                @for (rail of railShares(); track rail.rail) {
                <div class="p-3.5 rounded-xl border border-slate-100 bg-slate-50/80 flex flex-col justify-between">
                    <div class="flex items-center gap-1.5">
                        <span class="w-2.5 h-2.5 rounded-full" [class]="rail.colorClass"></span>
                        <span class="text-xs font-semibold text-slate-700 truncate">{{ rail.labelKey | translate }}</span>
                    </div>
                    <div class="mt-2.5">
                        <div class="text-lg font-bold text-slate-900">
                            {{ rail.amount | currency: currencyCode() : 'symbol-narrow' : '1.2-2' }}
                        </div>
                        <div class="text-[11px] text-slate-500 mt-0.5">
                            {{ rail.count }} payments ({{ rail.sharePct.toFixed(1) }}%)
                        </div>
                    </div>
                </div>
                }
            </div>
        </div>

        <!-- 2. Obligation Status Breakdown -->
        <div class="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
            <div>
                <h3 class="text-base font-semibold text-slate-900">{{ 'reports.statusDistTitle' | translate }}</h3>
                <p class="text-xs text-slate-500 mt-0.5">{{ 'reports.statusDistSubtitle' | translate }}</p>
            </div>

            @if (obligationStats(); as ob) {
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div class="p-3 rounded-xl border border-slate-100 bg-emerald-50/50">
                    <span class="text-[11px] font-semibold uppercase text-emerald-700">Paid / Settled</span>
                    <div class="text-xl font-bold text-slate-900 mt-1.5">{{ ob.statusCounts?.['Paid'] || 0 }}</div>
                    <span class="text-[10px] text-emerald-600 font-medium">Successfully collected</span>
                </div>

                <div class="p-3 rounded-xl border border-slate-100 bg-blue-50/50">
                    <span class="text-[11px] font-semibold uppercase text-blue-700">Unpaid / Open</span>
                    <div class="text-xl font-bold text-slate-900 mt-1.5">{{ ob.statusCounts?.['Open'] || 0 }}</div>
                    <span class="text-[10px] text-blue-600 font-medium">Awaiting payment</span>
                </div>

                <div class="p-3 rounded-xl border border-slate-100 bg-amber-50/50">
                    <span class="text-[11px] font-semibold uppercase text-amber-700">Under Review</span>
                    <div class="text-xl font-bold text-slate-900 mt-1.5">{{ ob.statusCounts?.['HeldForReview'] || 0 }}</div>
                    <span class="text-[10px] text-amber-600 font-medium">Operator review</span>
                </div>

                <div class="p-3 rounded-xl border border-slate-100 bg-rose-50/50">
                    <span class="text-[11px] font-semibold uppercase text-rose-700">Expired</span>
                    <div class="text-xl font-bold text-slate-900 mt-1.5">{{ ob.statusCounts?.['Expired'] || 0 }}</div>
                    <span class="text-[10px] text-rose-600 font-medium">Pursued as violation</span>
                </div>

                <div class="p-3 rounded-xl border border-slate-100 bg-slate-50">
                    <span class="text-[11px] font-semibold uppercase text-slate-600">Forgiven</span>
                    <div class="text-xl font-bold text-slate-900 mt-1.5">{{ ob.statusCounts?.['Waived'] || 0 }}</div>
                    <span class="text-[10px] text-slate-500 font-medium">Goodwill waiver</span>
                </div>

                <div class="p-3 rounded-xl border border-slate-100 bg-slate-50">
                    <span class="text-[11px] font-semibold uppercase text-slate-600">Cancelled</span>
                    <div class="text-xl font-bold text-slate-900 mt-1.5">{{ ob.statusCounts?.['Cancelled'] || 0 }}</div>
                    <span class="text-[10px] text-slate-500 font-medium">Invalidated</span>
                </div>
            </div>
            }
        </div>
    </div>
</div>
